import { registerDestructor } from '@ember/destroyable';
import { hash } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { trackedMap } from '@ember/reactive/collections';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismCard,
  PrismIcon,
  PrismInput,
  PrismMessage,
  PrismTable,
} from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
// @ts-expect-error: Incorrect type
import InfiniteScroller from '@zestia/ember-simple-infinite-scroller/components/infinite-scroller';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import GeneralTablesCellsTdTwoLine from 'lumen/components/general/tables/cells/td-two-line';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import InlineFilters from 'lumen/components/settings/users/inline-filters';
import type { PropertyOption } from 'lumen/components/settings/users/property-select';
import BulkActionRow from 'lumen/components/settings/users/tables/bulk-action-row';
import CellActions from 'lumen/components/settings/users/tables/cells/cell-actions';
import CellProductAccess from 'lumen/components/settings/users/tables/cells/cell-product-access';
import CellRole from 'lumen/components/settings/users/tables/cells/cell-role';
import CellRoleReadOnly from 'lumen/components/settings/users/tables/cells/cell-role-read-only';
import type AggregatedUser from 'lumen/models/aggregated-user';
import type {
  default as SubscriptionUser,
  UserProducts,
} from 'lumen/models/subscription-user';
import GetElement from 'lumen/modifiers/get-element';
import {
  type AccessRight,
  MIXED,
  type MixedRole,
  PRODUCTS,
} from 'lumen/utils/constants/users-constants';
import {
  setupUnsavedChangesTransitionHandler,
  type TransitionHandler,
} from 'lumen/utils/unsaved-changes-transition-handlers';

const COLUMNS = {
  NAME: 'name',
  ROLE: 'role',
  RATE_AND_MARKET_INSIGHT: 'rateAndMarketInsight',
  BI_BENCHMARKING: 'biBenchmarking',
  LAST_ACTIVE: 'lastActive',
  ACTIONS: 'actions',
} as const;

function hasProduct(
  products: UserProducts | null,
  product: keyof UserProducts,
): boolean {
  return products?.[product]?.is_accessible ?? false;
}

function toProductAccessState(hasAccess: boolean, isLocked: boolean): string {
  if (!hasAccess) {
    return 'unchecked';
  }
  if (isLocked) {
    return 'readonly';
  }
  return 'checked';
}

export interface UserChange {
  email: string;
  name: string;
  newBiBenchmarking: boolean;
  newRateAndMarketInsight: boolean;
  newRole: AccessRight;
  originalBiBenchmarking: boolean;
  originalRateAndMarketInsight: boolean;
  originalRole: AccessRight;
  userId: string;
}

interface UserRow {
  biBenchmarking: string;
  disableCheckboxSelection?: boolean;
  hasBiBenchmarkingAccess: boolean;
  hasRateInsightAccess: boolean;
  isDisabled?: boolean;
  isLocked?: boolean;
  name: { primary: string; secondary: string };
  onProductChange?: (valuePath: string, checked: boolean) => void;
  onRoleChange?: (role: AccessRight) => void;
  rateAndMarketInsight: string;
  role: AccessRight;
  userId: string;
}

interface RowOverride {
  biBenchmarking?: boolean;
  rateAndMarketInsight?: boolean;
  role?: AccessRight;
}

interface UsersTableSignature {
  Args: {
    aggregatedUserCount: number;
    aggregatedUsers: AggregatedUser[];
    displaySearchValue: string;
    isAdmin: boolean;
    isAllProperties: boolean;
    isLoading: boolean;
    onDeleteUser: (user: SubscriptionUser) => Promise<void>;
    onInvite: () => void;
    onLoadMore: () => Promise<void>;
    onProductChange: (product: string | null) => void;
    onPropertiesChange: (option: PropertyOption | null) => void;
    onRoleChange: (role: string | null) => void;
    onSave: (changes: UserChange[]) => Promise<boolean>;
    onSearchChange: (event: Event) => void;
    onSortChange: (
      sorts: { isAscending: boolean; valuePath: string }[],
    ) => void;
    product: string | null;
    role: string | null;
    selectedProperties: PropertyOption | null;
    sorts: { isAscending: boolean; valuePath: string }[];
    users: SubscriptionUser[];
  };
}

export default class SettingsUsersTablesUsersTable extends Component<UsersTableSignature> {
  @service declare alerting: Services['alerting'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  @tracked bulkBiBenchmarkingOverride: boolean | undefined;
  @tracked bulkRateAndMarketInsightOverride: boolean | undefined;
  @tracked bulkRoleOverride: AccessRight | undefined;
  @tracked isSaving = false;

  @tracked rowSelection: CustomEmberTableRow[] = [];
  @tracked tableScrollEl: HTMLElement | null = null;
  #transitionHandler?: TransitionHandler;
  private rowOverrides = trackedMap<string, RowOverride>();

  private get adminColumns(): CustomEmberTableColumn[] {
    return [
      {
        name: this.intl.t('settings.users.column.name'),
        valuePath: COLUMNS.NAME,
        tdComponent: GeneralTablesCellsTdTwoLine,
        textAlign: 'left' as const,
        width: 260,
      },
      {
        name: this.intl.t('settings.users.column.user_role'),
        valuePath: COLUMNS.ROLE,
        tdComponent: CellRole,
        textAlign: 'left' as const,
        width: 200,
      },
      {
        name: this.intl.t('settings.users.column.rate_insight_market_insight'),
        valuePath: COLUMNS.RATE_AND_MARKET_INSIGHT,
        tdComponent: CellProductAccess,
        textAlign: 'left' as const,
        isSortable: false,
      },
      {
        name: this.intl.t('settings.users.column.bi_benchmarking'),
        valuePath: COLUMNS.BI_BENCHMARKING,
        tdComponent: CellProductAccess,
        textAlign: 'left' as const,
        isSortable: false,
      },
      {
        name: this.intl.t('settings.users.column.last_active'),
        valuePath: COLUMNS.LAST_ACTIVE,
        textAlign: 'left' as const,
        isSortable: false,
        width: 160,
      },
      {
        name: '',
        valuePath: COLUMNS.ACTIONS,
        tdComponent: CellActions,
        textAlign: 'left' as const,
        isSortable: false,
        width: 40,
      },
    ];
  }

  private get aggregatedColumns(): CustomEmberTableColumn[] {
    return [
      {
        name: this.intl.t('settings.users.column.name'),
        valuePath: COLUMNS.NAME,
        tdComponent: GeneralTablesCellsTdTwoLine,
        textAlign: 'left' as const,
        width: 260,
      },
      {
        name: this.intl.t('settings.users.column.rate_insight_market_insight'),
        valuePath: COLUMNS.RATE_AND_MARKET_INSIGHT,
        textAlign: 'left' as const,
        isSortable: false,
      },
      {
        name: this.intl.t('settings.users.column.bi_benchmarking'),
        valuePath: COLUMNS.BI_BENCHMARKING,
        textAlign: 'left' as const,
        isSortable: false,
      },
      {
        name: this.intl.t('settings.users.column.last_active'),
        valuePath: COLUMNS.LAST_ACTIVE,
        textAlign: 'left' as const,
        isSortable: false,
        width: 160,
      },
      {
        name: '',
        valuePath: COLUMNS.ACTIONS,
        tdComponent: CellActions,
        textAlign: 'left' as const,
        isSortable: false,
        width: 40,
      },
    ];
  }

  private get aggregatedRows(): CustomEmberTableRow[] {
    return this.args.aggregatedUsers.map((user) => {
      return {
        [COLUMNS.NAME]: {
          primary: `${user.firstName} ${user.lastName}`,
          secondary: user.email,
        },
        [COLUMNS.RATE_AND_MARKET_INSIGHT]: this.formatPropertyCount(
          user.rateInsightPropertyCount,
        ),
        [COLUMNS.BI_BENCHMARKING]: this.formatPropertyCount(
          user.biBenchmarkingPropertyCount,
        ),
        [COLUMNS.LAST_ACTIVE]: user.lastActive
          ? dayjs(user.lastActive).format('DD MMM YYYY')
          : this.intl.t('general.missing'),
        [COLUMNS.ACTIONS]: '',
        hideRemoveUser: true,
        onViewPropertyAccess: () => this.navigateToPropertyAccess(user),
      };
    });
  }

  @cached
  get allChanges(): UserChange[] {
    if (this.args.users.length === 0) {
      return [];
    }

    const changes = new Map<string, UserChange>();

    for (const [userId, override] of this.rowOverrides) {
      const user = this.args.users.find(
        (userRecord) => userRecord.id === userId,
      );
      if (!user) {
        continue;
      }

      const originalRole = user.role;
      const originalRateAndMarketInsight = hasProduct(
        user.products,
        PRODUCTS.RATE_INSIGHT,
      );
      const originalBiBenchmarking = hasProduct(
        user.products,
        PRODUCTS.BI_BENCHMARKING,
      );

      changes.set(userId, {
        userId,
        name: user.name,
        email: user.email,
        originalRole,
        newRole: override.role ?? originalRole,
        originalRateAndMarketInsight,
        newRateAndMarketInsight:
          override.rateAndMarketInsight ?? originalRateAndMarketInsight,
        originalBiBenchmarking,
        newBiBenchmarking: override.biBenchmarking ?? originalBiBenchmarking,
      });
    }

    for (const untypedRow of this.rowSelection) {
      const row = untypedRow as unknown as UserRow;
      if (changes.has(row.userId)) {
        continue;
      }

      const originalRole = row.role;
      const newRole = this.bulkRoleOverride ?? originalRole;
      const originalRateAndMarketInsight = row.hasRateInsightAccess;
      const newRateAndMarketInsight =
        this.bulkRateAndMarketInsightOverride ?? originalRateAndMarketInsight;
      const originalBiBenchmarking = row.hasBiBenchmarkingAccess;
      const newBiBenchmarking =
        this.bulkBiBenchmarkingOverride ?? originalBiBenchmarking;

      const hasChange =
        newRole !== originalRole ||
        newRateAndMarketInsight !== originalRateAndMarketInsight ||
        newBiBenchmarking !== originalBiBenchmarking;

      if (hasChange) {
        changes.set(row.userId, {
          userId: row.userId,
          name: row.name.primary,
          email: row.name.secondary,
          originalRole,
          newRole,
          originalRateAndMarketInsight,
          newRateAndMarketInsight,
          originalBiBenchmarking,
          newBiBenchmarking,
        });
      }
    }

    return [...changes.values()];
  }

  get bulkBiBenchmarking(): { checked: boolean; indeterminate: boolean } {
    return this.computeBulkProductState(COLUMNS.BI_BENCHMARKING);
  }

  get bulkRateAndMarketInsight(): { checked: boolean; indeterminate: boolean } {
    return this.computeBulkProductState(COLUMNS.RATE_AND_MARKET_INSIGHT);
  }

  get bulkRole(): { isIndeterminate: boolean; value: AccessRight | MixedRole } {
    if (this.bulkRoleOverride !== undefined) {
      return { isIndeterminate: false, value: this.bulkRoleOverride };
    }

    const roles = this.rowSelection.map(
      (row) => (row as unknown as UserRow).role,
    );
    const uniqueRoles = [...new Set(roles)];
    const firstRole = uniqueRoles[0];

    if (uniqueRoles.length === 1 && firstRole) {
      return { isIndeterminate: false, value: firstRole };
    }

    return { isIndeterminate: true, value: MIXED };
  }

  get columns(): CustomEmberTableColumn[] {
    if (this.args.isAllProperties) {
      return this.aggregatedColumns;
    }

    return this.args.isAdmin ? this.adminColumns : this.viewerColumns;
  }

  private get hasBulkOverrides(): boolean {
    return (
      this.rowSelection.length > 0 &&
      (this.bulkRoleOverride !== undefined ||
        this.bulkRateAndMarketInsightOverride !== undefined ||
        this.bulkBiBenchmarkingOverride !== undefined)
    );
  }

  get hasPendingChanges(): boolean {
    return this.rowOverrides.size > 0 || this.hasBulkOverrides;
  }

  get hasSelection(): boolean {
    return this.rowSelection.length > 0;
  }

  get hasUnsavedChanges(): boolean {
    return this.hasPendingChanges;
  }

  get isSaveDisabled(): boolean {
    return !!this.validationError || this.isSaving || !this.hasPendingChanges;
  }

  get rows(): CustomEmberTableRow[] {
    if (this.args.isAllProperties) {
      return this.aggregatedRows;
    }

    if (this.args.users.length === 0) {
      return [];
    }

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const component = this;

    return this.args.users.map((user) => {
      const userId = user.id as string;
      const isLocked = user.isLocked ?? false;
      const userRateInsightAccess = hasProduct(
        user.products,
        PRODUCTS.RATE_INSIGHT,
      );
      const userBiBenchmarkingAccess = hasProduct(
        user.products,
        PRODUCTS.BI_BENCHMARKING,
      );

      const override = this.rowOverrides.get(userId);
      const effectiveRole = override?.role ?? user.role;
      const effectiveRateAndMarketInsight =
        override?.rateAndMarketInsight ?? userRateInsightAccess;
      const effectiveBiBenchmarking =
        override?.biBenchmarking ?? userBiBenchmarkingAccess;

      return {
        userId,
        isLocked,
        hasBiBenchmarkingAccess: userBiBenchmarkingAccess,
        hasRateInsightAccess: userRateInsightAccess,
        originalRole: user.role,
        originalValues: {
          [COLUMNS.RATE_AND_MARKET_INSIGHT]: toProductAccessState(
            userRateInsightAccess,
            isLocked,
          ),
          [COLUMNS.BI_BENCHMARKING]: toProductAccessState(
            userBiBenchmarkingAccess,
            isLocked,
          ),
        },
        [COLUMNS.NAME]: {
          primary: user.name,
          secondary: user.email,
        },
        [COLUMNS.ROLE]: effectiveRole,
        [COLUMNS.RATE_AND_MARKET_INSIGHT]: toProductAccessState(
          effectiveRateAndMarketInsight,
          isLocked,
        ),
        [COLUMNS.BI_BENCHMARKING]: toProductAccessState(
          effectiveBiBenchmarking,
          isLocked,
        ),
        [COLUMNS.LAST_ACTIVE]: user.lastLogin
          ? dayjs(user.lastLogin).format('DD MMM YYYY')
          : this.intl.t('general.missing'),
        [COLUMNS.ACTIONS]: '',
        disableCheckboxSelection: isLocked,
        get isDisabled(): boolean {
          return isLocked || component.hasSelection;
        },
        onDelete: (): void => {
          void this.args.onDeleteUser(user);
        },
        onViewPropertyAccess: () => this.navigateToPropertyAccess(user),
        onRoleChange: (role: AccessRight): void => {
          if (component.hasSelection) {
            return;
          }
          this.updateRowOverride(userId, { role }, user);
        },
        onProductChange: (valuePath: string, checked: boolean): void => {
          if (component.hasSelection) {
            return;
          }
          const key =
            valuePath === COLUMNS.RATE_AND_MARKET_INSIGHT
              ? 'rateAndMarketInsight'
              : 'biBenchmarking';
          this.updateRowOverride(userId, { [key]: checked }, user);
        },
      };
    });
  }

  get validationError(): string | undefined {
    const hasInvalidChange = this.allChanges.some(
      (change) => !change.newRateAndMarketInsight && !change.newBiBenchmarking,
    );

    return hasInvalidChange
      ? this.intl.t('settings.users.edit.min_product_error')
      : undefined;
  }

  private get viewerColumns(): CustomEmberTableColumn[] {
    return [
      {
        name: this.intl.t('settings.users.column.name'),
        valuePath: COLUMNS.NAME,
        tdComponent: GeneralTablesCellsTdTwoLine,
        textAlign: 'left' as const,
        width: 260,
      },
      {
        name: this.intl.t('settings.users.column.user_role'),
        valuePath: COLUMNS.ROLE,
        tdComponent: CellRoleReadOnly,
        textAlign: 'left' as const,
        isSortable: false,
        width: 200,
      },
    ];
  }

  constructor(owner: Owner, args: UsersTableSignature['Args']) {
    super(owner, args);

    this.#transitionHandler = setupUnsavedChangesTransitionHandler(
      this.router,
      this,
      'hotel.settings.users',
    );

    registerDestructor(this, () => {
      this.#transitionHandler?.cleanup();
      this.#transitionHandler = undefined;
    });
  }

  private computeBulkProductState(
    column:
      | typeof COLUMNS.RATE_AND_MARKET_INSIGHT
      | typeof COLUMNS.BI_BENCHMARKING,
  ): { checked: boolean; indeterminate: boolean } {
    if (this.rowSelection.length === 0) {
      return { checked: false, indeterminate: false };
    }

    const bulkOverride =
      column === COLUMNS.RATE_AND_MARKET_INSIGHT
        ? this.bulkRateAndMarketInsightOverride
        : this.bulkBiBenchmarkingOverride;

    if (bulkOverride !== undefined) {
      return { checked: bulkOverride, indeterminate: false };
    }

    const states = this.rowSelection.map(
      (row) => (row as unknown as UserRow)[column],
    );
    const allChecked = states.every((state) => state === 'checked');
    const allUnchecked = states.every((state) => state === 'unchecked');

    if (allChecked) {
      return { checked: true, indeterminate: false };
    }
    if (allUnchecked) {
      return { checked: false, indeterminate: false };
    }

    return { checked: false, indeterminate: true };
  }

  private formatPropertyCount(count: number): string {
    return count > 0
      ? this.intl.t('settings.users.property_count', { count })
      : this.intl.t('general.missing');
  }

  private resetAll(): void {
    this.rowOverrides.clear();
    this.rowSelection = [];
    this.bulkRoleOverride = undefined;
    this.bulkRateAndMarketInsightOverride = undefined;
    this.bulkBiBenchmarkingOverride = undefined;
  }

  private resetBulkOverrides(): void {
    this.bulkRoleOverride = undefined;
    this.bulkRateAndMarketInsightOverride = undefined;
    this.bulkBiBenchmarkingOverride = undefined;
  }

  private updateRowOverride(
    userId: string,
    update: Partial<RowOverride>,
    user: SubscriptionUser,
  ): void {
    const override = { ...this.rowOverrides.get(userId), ...update };
    const userRateInsightAccess = hasProduct(
      user.products,
      PRODUCTS.RATE_INSIGHT,
    );
    const userBiBenchmarkingAccess = hasProduct(
      user.products,
      PRODUCTS.BI_BENCHMARKING,
    );

    if (override.role === user.role) {
      delete override.role;
    }
    if (override.rateAndMarketInsight === userRateInsightAccess) {
      delete override.rateAndMarketInsight;
    }
    if (override.biBenchmarking === userBiBenchmarkingAccess) {
      delete override.biBenchmarking;
    }

    if (Object.keys(override).length > 0) {
      this.rowOverrides.set(userId, override);
    } else {
      this.rowOverrides.delete(userId);
    }
  }

  @action
  cancelAll(): void {
    this.resetAll();
  }

  @action
  discardChanges(): void {
    this.resetAll();
  }

  @action
  handleBulkBiBenchmarkingToggle(): void {
    this.bulkBiBenchmarkingOverride = !this.bulkBiBenchmarking.checked;
  }

  @action
  handleBulkRateAndMarketInsightToggle(): void {
    this.bulkRateAndMarketInsightOverride =
      !this.bulkRateAndMarketInsight.checked;
  }

  @action
  handleBulkRoleChange(option: Option): void {
    this.bulkRoleOverride = option.key as AccessRight;
  }

  @action
  handleRowSelect(
    rowSelection: CustomEmberTableRow | CustomEmberTableRow[],
  ): void {
    if (this.isSaving) {
      return;
    }

    this.resetBulkOverrides();

    const selectedRows = Array.isArray(rowSelection)
      ? rowSelection
      : [rowSelection];

    if (selectedRows.length === 0 && this.rowOverrides.size > 0) {
      this.rowOverrides.clear();
    }

    this.rowSelection = selectedRows;
  }

  @action
  navigateToPropertyAccess(user: AggregatedUser | SubscriptionUser): void {
    this.router.transitionTo('hotel.settings.users.property-access', {
      queryParams: {
        userId: user.id,
        userEmail: user.email,
      },
    });
  }

  @action
  registerTableContainer(el: HTMLElement): void {
    this.tableScrollEl = el.querySelector<HTMLElement>('.ember-table-overflow');
  }

  @action
  async saveAll(): Promise<void> {
    if (this.validationError || this.allChanges.length === 0) {
      return;
    }

    this.isSaving = true;

    try {
      const success = await this.args.onSave(this.allChanges);

      if (success) {
        this.resetAll();
      }
    } finally {
      this.isSaving = false;
    }
  }

  @action
  showDiscardChangesAlert(): Promise<boolean> {
    return this.alerting.showConfirmWarningAlert({
      title: this.intl.t('general.unsaved_changes.title'),
      text: this.intl.t('general.unsaved_changes.description'),
      buttons: {
        cancel: { text: this.intl.t('general.cancel') },
        confirm: { text: this.intl.t('general.discard_changes') },
      },
    });
  }

  <template>
    <SettingsPageTemplate
      @description={{t "settings.users.description"}}
      @isFullHeight={{true}}
      @title={{t "settings.users.title"}}
      data-test-id="settings-users-page"
    >
      <:headerAfter>
        <div class="flex items-end gap-200 self-end">
          {{#if this.hasPendingChanges}}
            <PrismButton
              @buttonType="secondary"
              @label={{t "general.cancel"}}
              @onClick={{this.cancelAll}}
            />
            <PrismButton
              @buttonType="primary"
              @isDisabled={{this.isSaveDisabled}}
              @label={{t "settings.users.bulk.save"}}
              @onClick={{this.saveAll}}
            />
          {{else if @isAdmin}}
            <PrismButton
              @buttonType="primary"
              @iconBefore="plus"
              @label={{t "settings.users.invite_users"}}
              @onClick={{@onInvite}}
            />
          {{/if}}
        </div>
      </:headerAfter>

      <:default>
        <div class="flex h-full flex-col">
          {{#if this.validationError}}
            <div class="mb-200 flex items-center gap-100" role="alert">
              <PrismIcon @color="critical" @name="warning" @size="small" />
              <span class="text-200--medium text-critical-default">
                {{this.validationError}}
              </span>
            </div>
          {{/if}}
          <PrismCard @contentLayout="noPadding">
            <:content>
              <div class="flex h-full flex-col overflow-hidden">
                <div
                  class="flex shrink-0 items-center justify-end gap-200 px-400 py-300"
                >
                  {{#if @isAdmin}}
                    <InlineFilters
                      @isAllProperties={{@isAllProperties}}
                      @onProductChange={{@onProductChange}}
                      @onPropertiesChange={{@onPropertiesChange}}
                      @onRoleChange={{@onRoleChange}}
                      @onSearchChange={{@onSearchChange}}
                      @product={{@product}}
                      @properties={{@selectedProperties}}
                      @role={{@role}}
                      @search={{@displaySearchValue}}
                      @userCount={{@aggregatedUserCount}}
                    />
                  {{else}}
                    <div>
                      <PrismInput
                        @iconAfter={{hash name="search"}}
                        @onInput={{@onSearchChange}}
                        @placeholder={{t
                          "settings.users.filter.search_placeholder"
                        }}
                        @removePadding={{true}}
                        @value={{@displaySearchValue}}
                      />
                    </div>
                  {{/if}}
                </div>
                <InfiniteScroller
                  @element={{this.tableScrollEl}}
                  @onLoadMore={{@onLoadMore}}
                  @percent={{90}}
                  class="flex min-h-0 flex-1 flex-col"
                >
                  <PrismTable
                    {{GetElement this.registerTableContainer}}
                    @checkboxSelectionMode={{if
                      (and @isAdmin (not @isAllProperties))
                      "multiple"
                      "none"
                    }}
                    @columns={{this.columns}}
                    @isLoading={{@isLoading}}
                    @onRowSelect={{this.handleRowSelect}}
                    @onUpdateSorts={{@onSortChange}}
                    @oneCellBulkEditRow={{false}}
                    @rowSelection={{unless @isLoading this.rowSelection}}
                    @rows={{this.rows}}
                    @sorts={{@sorts}}
                    @widthConstraint="gte-container-slack"
                  >
                    <:bulkEditRow as |bulkEditRow|>
                      <BulkActionRow
                        @bulkBiBenchmarking={{this.bulkBiBenchmarking}}
                        @bulkRateAndMarketInsight={{this.bulkRateAndMarketInsight}}
                        @bulkRole={{this.bulkRole}}
                        @numberOfSelectedRows={{bulkEditRow.numberOfSelectedRows}}
                        @onBulkBiBenchmarkingToggle={{this.handleBulkBiBenchmarkingToggle}}
                        @onBulkRateAndMarketInsightToggle={{this.handleBulkRateAndMarketInsightToggle}}
                        @onBulkRoleChange={{this.handleBulkRoleChange}}
                      />
                    </:bulkEditRow>
                    <:emptyState>
                      <div class="flex h-full items-center justify-center">
                        <PrismMessage
                          @illustration="100x100_search"
                          @title={{t
                            "settings.users.empty.nothing_matches_filters"
                          }}
                        />
                      </div>
                    </:emptyState>
                  </PrismTable>
                </InfiniteScroller>
              </div>
            </:content>
          </PrismCard>
        </div>
      </:default>
    </SettingsPageTemplate>
  </template>
}
